import React, { useState, useEffect } from 'react';

// Recreated Polymet-style MapFilters with local UI (no external libs)
// Props contract:
// - filters: { search, domains, cepaCategories, statuses, dueDateFilter, assigneeIds, inSprintOnly }
// - onFiltersChange(next)
// - itemCounts: { total, byDomain, byCEPA, byStatus, overdue, dueNext7Days, dueThisMonth, inSprint }
const MapFilters = ({ filters, onFiltersChange, itemCounts, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || '');

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchValue !== (filters.search || '')) {
        onFiltersChange({ ...filters, search: searchValue });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchValue]);

  const toggleStatus = (statusId) => {
    const exists = (filters.statuses || []).includes(statusId);
    const next = exists ? (filters.statuses || []).filter((s) => s !== statusId) : [ ...(filters.statuses || []), statusId ];
    onFiltersChange({ ...filters, statuses: next });
  };

  const handleDueQuick = (key) => onFiltersChange({ ...filters, dueDateFilter: key });

  const clearAll = () => {
    onFiltersChange({
      search: '', domains: [], cepaCategories: [], statuses: ['open','in_progress','blocked'],
      dueDateFilter: 'all', assigneeIds: [], inSprintOnly: false,
    });
    setSearchValue('');
  };

  const activeFilterCount = [
    filters.search,
    (filters.domains || []).length > 0 ? 'domains' : '',
    (filters.cepaCategories || []).length > 0 ? 'cepa' : '',
    JSON.stringify(filters.statuses || []) !== JSON.stringify(['open','in_progress','blocked']) ? 'statuses' : '',
    filters.dueDateFilter !== 'all' ? 'due' : '',
    (filters.assigneeIds || []).length > 0 ? 'assignees' : '',
    filters.inSprintOnly ? 'sprint' : '',
  ].filter(Boolean).length;

  return (
    <div className={`bg-succession-bg-secondary border-b border-succession-border ${className}`}>
      <div className="p-4">
        {/* Search & Toggle */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[220px]">
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2 text-sm"
            />
          </div>
          <button
            className="border border-succession-border rounded-md px-3 py-2 text-sm text-succession-text-primary"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
          </button>
          {activeFilterCount > 0 && (
            <button className="text-sm px-2 py-2 text-succession-text-secondary" onClick={clearAll}>Clear</button>
          )}
        </div>

        {/* Quick Chips */}
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            className={`text-xs px-3 py-1 rounded border bg-succession-bg-primary ${filters.dueDateFilter === 'overdue' ? 'border-succession-text-primary' : 'border-succession-border'}`}
            onClick={() => handleDueQuick('overdue')}
          >
            Overdue ({itemCounts?.overdue || 0})
          </button>
          <button
            className={`text-xs px-3 py-1 rounded border bg-succession-bg-primary ${filters.dueDateFilter === 'next_7_days' ? 'border-succession-text-primary' : 'border-succession-border'}`}
            onClick={() => handleDueQuick('next_7_days')}
          >
            Next 7 days ({itemCounts?.dueNext7Days || 0})
          </button>
          <button
            className={`text-xs px-3 py-1 rounded border bg-succession-bg-primary ${filters.dueDateFilter === 'this_month' ? 'border-succession-text-primary' : 'border-succession-border'}`}
            onClick={() => handleDueQuick('this_month')}
          >
            This month ({itemCounts?.dueThisMonth || 0})
          </button>
          <button
            className={`text-xs px-3 py-1 rounded border bg-succession-bg-primary ${filters.inSprintOnly ? 'border-succession-text-primary' : 'border-succession-border'}`}
            onClick={() => onFiltersChange({ ...filters, inSprintOnly: !filters.inSprintOnly })}
          >
            In Sprint ({itemCounts?.inSprint || 0})
          </button>
        </div>

        {/* Expanded Filters */}
        {isExpanded && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Statuses */}
            <div>
              <div className="text-sm font-medium mb-2 text-succession-text-primary">Status</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'open', label: 'Open' },
                  { id: 'in_progress', label: 'In Progress' },
                  { id: 'blocked', label: 'Blocked' },
                  { id: 'completed', label: 'Completed' },
                  { id: 'wont_do', label: "Won't Do" },
                ].map((s) => (
                  <button
                    key={s.id}
                    className={`text-xs px-3 py-1 rounded border ${
                      (filters.statuses || []).includes(s.id) ? 'bg-succession-text-primary text-white border-succession-text-primary' : 'border-succession-border text-succession-text-primary'
                    }`}
                    onClick={() => toggleStatus(s.id)}
                  >
                    {s.label} ({itemCounts?.byStatus?.[s.id] || 0})
                  </button>
                ))}
              </div>
            </div>

            {/* Domain & VMI */}
            <div>
              <div className="text-sm font-medium mb-2 text-succession-text-primary">Domain</div>
              <div className="flex flex-wrap gap-2">
                {['personal','business'].map((d) => (
                  <button
                    key={d}
                    className={`text-xs px-3 py-1 rounded border ${
                      d === 'business' ? 'bg-succession-success-bg text-succession-success border-emerald-200' : 'bg-succession-primary-10 text-succession-primary border-blue-200'
                    } ${ (filters.domains || []).includes(d) ? 'ring-1 ring-succession-border' : ''}`}
                    onClick={() => {
                      const has = (filters.domains || []).includes(d);
                      const next = has ? (filters.domains || []).filter((x) => x !== d) : [ ...(filters.domains || []), d ];
                      onFiltersChange({ ...filters, domains: next });
                    }}
                  >
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-2 text-succession-text-primary">VMI Category</div>
              <div className="flex flex-wrap gap-2">
                {['identify','protect','build','harvest','manage'].map((c) => {
                  const color = c === 'identify' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                c === 'protect' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                c === 'build' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                c === 'harvest' ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-gray-50 text-gray-700 border-gray-200';
                  const active = (filters.cepaCategories || []).includes(c);
                  return (
                    <button
                      key={c}
                      className={`text-xs px-3 py-1 rounded border ${color} ${active ? 'ring-1 ring-succession-border' : ''}`}
                      onClick={() => {
                        const has = (filters.cepaCategories || []).includes(c);
                        const next = has ? (filters.cepaCategories || []).filter((x) => x !== c) : [ ...(filters.cepaCategories || []), c ];
                        onFiltersChange({ ...filters, cepaCategories: next });
                      }}
                    >
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date Range */}
            <div>
              <div className="text-sm font-medium mb-2 text-succession-text-primary">Date Range</div>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-2 py-1 text-sm"
                  value={filters.startDate || ''}
                  onChange={(e) => onFiltersChange({ ...filters, startDate: e.target.value || undefined })}
                />
                <span className="text-xs text-gray-500">to</span>
                <input
                  type="date"
                  className="border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-2 py-1 text-sm"
                  value={filters.endDate || ''}
                  onChange={(e) => onFiltersChange({ ...filters, endDate: e.target.value || undefined })}
                />
              </div>
            </div>

            {/* Results count */}
            <div>
              <div className="text-sm font-medium mb-2 text-succession-text-primary">Results</div>
              <div className="text-xs text-succession-text-secondary">Showing {itemCounts?.total || 0} items</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapFilters;


